/**
 * PURPOSE: Seleccionar sedes sin desbordar modales ni perder la dirección como contexto.
 * RESPONSIBILITIES: Mostrar solo el nombre seleccionado, renderizar nombre/dirección truncados, buscar por ambos campos y permitir scroll en la lista.
 * COLLABORATORS: MatchPlannerModal.jsx, ModalProgramacion.jsx y react-select.
 * ROUTE: winscore/src/components/Inputs/VenueSelect.jsx
 */
import React, { useMemo } from "react";
import Select, { components } from "react-select";

const normalizeVenue = (venue) => {
  const id = Number(venue?.idsede || venue?.id || venue?.value);
  if (!Number.isInteger(id) || id <= 0) return null;

  const name = String(venue?.name || venue?.label || "Sede sin nombre").trim();
  const address = String(venue?.address || "").trim();

  return {
    ...venue,
    id,
    idsede: id,
    value: id,
    name,
    address: address || null,
    label: name,
    searchText: `${name} ${address}`.trim(),
  };
};

const VenueOption = (props) => {
  const { data } = props;
  const title = [data.name, data.address].filter(Boolean).join(" — ");

  return (
    <components.Option {...props}>
      <div className="min-w-0" title={title}>
        <div className="truncate text-sm font-bold text-slate-800">
          {data.name}
        </div>

        {data.address ? (
          <div className="mt-0.5 truncate text-xs text-slate-500">
            {data.address}
          </div>
        ) : null}
      </div>
    </components.Option>
  );
};

const VenueSingleValue = (props) => (
  <components.SingleValue {...props}>
    <span
      className="block truncate"
      title={[props.data.name, props.data.address].filter(Boolean).join(" — ")}
    >
      {props.data.name}
    </span>
  </components.SingleValue>
);

const VenueSelect = ({
  venues = [],
  value,
  currentVenue = null,
  onChange,
  disabled = false,
  placeholder = "Seleccionar sede",
  className = "",
  inputId,
}) => {
  const options = useMemo(
    () => venues.map(normalizeVenue).filter(Boolean),
    [venues]
  );

  const selectedVenue = useMemo(() => {
    const selectedId = Number(value);

    if (!Number.isInteger(selectedId) || selectedId <= 0) {
      return null;
    }

    return (
      options.find((venue) => venue.idsede === selectedId) ||
      (Number(currentVenue?.idsede || currentVenue?.id) === selectedId
        ? normalizeVenue(currentVenue)
        : null)
    );
  }, [currentVenue, options, value]);

  const portalTarget =
    typeof document === "undefined" ? undefined : document.body;

  return (
    <div className={`min-w-0 max-w-full ${className}`}>
      <Select
        inputId={inputId}
        options={options}
        value={selectedVenue}
        onChange={(venue) => onChange?.(venue ? venue.idsede : "")}
        isDisabled={disabled}
        isClearable
        isSearchable
        placeholder={placeholder}
        components={{
          Option: VenueOption,
          SingleValue: VenueSingleValue,
        }}
        filterOption={({ data }, inputValue) =>
          data.searchText
            .toLocaleLowerCase("es-PE")
            .normalize("NFD")
            .replace(/[\u0300-\u036f]/g, "")
            .includes(
              String(inputValue || "")
                .toLocaleLowerCase("es-PE")
                .normalize("NFD")
                .replace(/[\u0300-\u036f]/g, "")
            )
        }
        noOptionsMessage={() => "No hay sedes disponibles"}
        menuPortalTarget={portalTarget}
        menuPosition={portalTarget ? "fixed" : "absolute"}
        menuPlacement="auto"
        maxMenuHeight={240}
        menuShouldScrollIntoView={false}
        captureMenuScroll
        classNamePrefix="venue-select"
        styles={{
          container: (base) => ({
            ...base,
            width: "100%",
            maxWidth: "100%",
            minWidth: 0,
          }),
          control: (base, state) => ({
            ...base,
            minHeight: 42,
            maxWidth: "100%",
            borderRadius: 12,
            borderColor: state.isFocused ? "#3b82f6" : "#cbd5e1",
            boxShadow: state.isFocused ? "0 0 0 2px #dbeafe" : "none",
            overflow: "hidden",
            "&:hover": {
              borderColor: "#3b82f6",
            },
          }),
          valueContainer: (base) => ({
            ...base,
            minWidth: 0,
            overflow: "hidden",
          }),
          singleValue: (base) => ({
            ...base,
            minWidth: 0,
            maxWidth: "100%",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }),
          input: (base) => ({
            ...base,
            minWidth: 0,
            maxWidth: "100%",
          }),
          menu: (base) => ({
            ...base,
            maxWidth: "min(92vw, 520px)",
          }),
          menuList: (base) => ({
            ...base,
            maxHeight: 240,
            overflowY: "auto",
            overscrollBehaviorY: "contain",
            WebkitOverflowScrolling: "touch",
          }),
          menuPortal: (base) => ({
            ...base,
            zIndex: 1800,
          }),
          option: (base) => ({
            ...base,
            minWidth: 0,
            overflow: "hidden",
          }),
        }}
      />
    </div>
  );
};

export default VenueSelect;